/* eslint-disable @typescript-eslint/no-non-null-assertion */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import {
  AppAlert,
  BlockUI,
  IMobileNumber,
  LaddaButton,
  SignUpFormValidator,
  defaultIMobileNumber,
  RecaptchaTokenAction,
  RecaptchaTerm,
  IBrowserError,
  RECAPTCHA_MAX_RETRIES,
  RECAPTCHA_RETRY_DELAY_MS,
  getRecaptchaToken,
  useCaptcha,
  IErrorMessage,
  RECAPTCHA_SCRIPT_LOAD_RETRIES,
  FaroFeatures,
  useLogger,
} from "@advapacs/common";
import React, {
  FC,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { defaultIndividualRequest, IRequestLanguage } from "@src/types/signup";
import { useSelector } from "react-redux";
import { IRootState } from "@src/config/reducers";
import { CurrentSignUpStep, SignUpAction } from "./ducks/slices";
import { SuccessRegister } from "./SuccessRegister";
import { useAppDispatch } from "@src/config/store";
import { BlockRouter } from "@src/components/BlockRouter";
import { AuthenticationLayout } from "@src/components/AuthenticationLayout";
import { SignUpLogo } from "./SignUpLogo";
import { StepPath } from "./StepPath";
import { SignUpCredentialsForm } from "./FormSteps/SignUpCredentialsForm";
import { PersonalInformationForm } from "./FormSteps/PersonalInformationForm";
import { PreferencesForm } from "./FormSteps/PreferencesForm";
import { getLanguages } from "../settingAccount/ducks/opertations";
import { signUpIndividual } from "./ducks/operations";
import { useLocation, useNavigate } from "react-router-dom";
import { SelectDomainType } from "@src/components/SelectDomainType";
import { TermAndServiceModal } from "@src/components/TermsAndService/TermAndServiceModal";
import { TermAndServiceWarning } from "@src/components/TermsAndService/TermAndServiceWarning";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { prodModeLog, reCaptchaDelay } from "@advapacs/common/src/utils/utils";
import {
  GOOGLE_RECAPTCHA_ENTERPRISE,
  TURNSTILE_SITE_KEY,
} from "@src/config/constants";
import { IndividualRegistrationRequest } from "@advapacs/clinician-api-protos/dist/Registration";
import { LogLevel } from "@grafana/faro-web-sdk";

export const SignUpIndividual: FC = () => {
  // ================== STATES ==========================
  const { t } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const executeRecaptchaRef = useRef(executeRecaptcha);
  const captchaType = useCaptcha();
  const [turnstileError, setTurnstileError] = useState("");
  const turnstileRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const accountRef = useRef(null);
  const personalInformationRef = useRef(null);
  const preferencesRef = useRef(null);
  const [email, setEmail] = useState("");
  const [socialToken, setSocialToken] = useState("");
  const [isSocialSignUp, setIsSocialSignUp] = useState(false);
  const [showCompletedRegister, setShowCompletedRegister] = useState(false);
  const [browserError, setBrowserError] = useState<IBrowserError | null>(null);
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location]
  );
  const { loading, individualResponse, errorMessage, eulaSignUpAgreement } =
    useSelector((state: IRootState) => state.signUpSlice);
  const { sessionHasBeenFetched } = useSelector(
    (state: IRootState) => state.loginReducer
  );
  const { languages } = useSelector(
    (state: IRootState) => state.settingAccountReducer
  );

  const tempTurnstileAction = useRef({
    action: "",
    data: {},
    lang: "",
    socialToken: "",
    mobilePhoneModel: defaultIMobileNumber,
  });

  const [dirty, setDirty] = useState(true);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [stepCount, setStepCount] = useState(1);
  const [currentStep, setCurrentStep] = useState(CurrentSignUpStep.CREDENTIAL);

  const validator = useMemo(() => {
    return SignUpFormValidator(t);
  }, [t]);
  const [, setError] = useState({});
  const [model, setModel] = useState<IRequestLanguage>(
    defaultIndividualRequest
  );
  const [mobilePhoneModel, setMobilePhoneModel] =
    useState<IMobileNumber>(defaultIMobileNumber);

  const validateTurnstile = () => {
    if (!turnstileRef?.current || !window.turnstile) {
      setCaptchaLoading(false);
      return false;
    }
    return true;
  };
  // ================== LOGGER ==========================
  const logger = useLogger("[SignUpIndividual]");
  /**
   * Step 1
   */
  const handleAccountInformation = () => {
    const {
      // @ts-ignore
      current: { validate, getData },
    } = accountRef;
    const { accountValidator } = validate();
    if (!accountValidator?.allValid()) {
      setError({});
      setStepCount(1);
      accountValidator?.showMessages();
      return;
    }
    accountValidator.hideMessages();
    const { accountModel } = getData();
    // Re-init model
    setModel({
      ...model,
      email: accountModel?.email,
      password: accountModel?.password,
      confirmPassword: accountModel?.confirmPassword,
    });
    // Next step
    setStepCount(2);
    setCurrentStep(CurrentSignUpStep.PERSONAL_INFORMATION);
  };
  /**
   * Step 2
   */
  const handlePersonalInformation = () => {
    const {
      // @ts-ignore
      current: { validate, getData },
    } = personalInformationRef;
    const { personalInformationValidator } = validate();
    if (!personalInformationValidator?.allValid()) {
      setError({});
      setStepCount(2);
      personalInformationValidator?.showMessages();
      return;
    }
    personalInformationValidator.hideMessages();
    const {
      personalInformationModel,
      mobilePhoneModel: personalMobilePhoneModel,
    } = getData();
    // Re-init model
    setModel({
      ...model,
      name: personalInformationModel?.name,
      practiceName: personalInformationModel?.practiceName,
      contactNumber: personalInformationModel?.mobilePhone,
    });
    setMobilePhoneModel(personalMobilePhoneModel);
    // Next step
    setStepCount(3);
    setCurrentStep(CurrentSignUpStep.PREFERENCES);
  };

  const generateRecaptchaToken = async (
    action: RecaptchaTokenAction,
    retries = RECAPTCHA_MAX_RETRIES
  ): Promise<string> => {
    const currentExecuteRecaptcha = executeRecaptchaRef.current; // alway fresh
    setBrowserError(null); // reset error message when starting generate token.
    setCaptchaLoading(true);
    // Script not loaded yet — retry until ready, then give up
    if (!currentExecuteRecaptcha) {
      if (retries > 0) {
        console.warn("Re-trigger waiting gRecaptcha loaded.");
        await reCaptchaDelay(RECAPTCHA_SCRIPT_LOAD_RETRIES);
        return generateRecaptchaToken(action, retries - 1);
      }
      setCaptchaLoading(false);
      logger({
        message: "reCAPTCHA script not loaded yet.",
        action: "generateRecaptchaToken",
        level: LogLevel.INFO,
        feature: FaroFeatures.RECAPTCHA,
      });
      return Promise.reject(new Error("reCAPTCHA script not loaded yet."));
    }
    try {
      return await getRecaptchaToken(
        action,
        GOOGLE_RECAPTCHA_ENTERPRISE,
        currentExecuteRecaptcha
      );
    } catch (error) {
      if (retries > 0) {
        const delayTime =
          RECAPTCHA_RETRY_DELAY_MS * (RECAPTCHA_MAX_RETRIES - retries + 1); // linear/exponential backoff
        console.warn(
          `reCAPTCHA failed. Retrying in ${
            delayTime / 1000
          }s... (${retries} retries left)`
        );
        await reCaptchaDelay(delayTime);
        return generateRecaptchaToken(action, retries - 1);
      }
      setCaptchaLoading(false);
      return Promise.reject((error as IErrorMessage)?.message || error);
    }
  };

  /**
   * Step 3
   */
  const handlePreferencesInformation = async () => {
    const {
      // @ts-ignore
      current: { validate, getData },
    } = preferencesRef;
    const { preferencesValidator } = validate();
    if (!preferencesValidator?.allValid()) {
      setError({});
      preferencesValidator?.showMessages();
      return;
    }
    preferencesValidator.hideMessages();
    const { preferencesModel } = getData();
    setModel({
      ...model,
      //ge: preferencesModel?.lang,
      defaultRegion: preferencesModel?.defaultRegion,
    });
    // validate recaptcha
    if (!validator.allValid()) {
      validator.showMessages();
      setError({});
      return;
    }

    setDirty(false);

    let requestModel = { ...model };

    if (isSocialSignUp) {
      requestModel = {
        ...model,
        email,
      };
    }

    if (captchaType?.provider === "turnstile") {
      if (!validateTurnstile()) {
        setTurnstileError("Turnstile not loaded in client Side.");
        return;
      }
      setTurnstileError(""); // reset error.
      // turnstile flow.
      window.turnstile?.reset(turnstileRef.current!); // reset before start
      tempTurnstileAction.current.action = "signUpIndividual"; //
      tempTurnstileAction.current.data = {
        ...requestModel,
        language: preferencesModel?.lang,
        defaultRegion: preferencesModel?.defaultRegion,
      };
      tempTurnstileAction.current.lang = preferencesModel?.lang;
      tempTurnstileAction.current.socialToken = socialToken;
      tempTurnstileAction.current.mobilePhoneModel = mobilePhoneModel;
      // trigger invisible challenge
      setCaptchaLoading(true); // trigger loading state.
      window.turnstile?.execute(turnstileRef.current!, {
        action: RecaptchaTokenAction.CP_REGISTRATION,
        async: false, // prevent async retries
      });
      setCaptchaLoading(false); // reset loading state.
      prodModeLog(
        "Trigger execute generate function from turnstile, (signUpIndividual function)"
      );
    } else {
      try {
        // avoid exception when trying generate recaptcha without ff.
        const recaptchaToken = await generateRecaptchaToken(
          RecaptchaTokenAction.CP_REGISTRATION
        );
        setCaptchaLoading(false);
        if (!recaptchaToken) throw Error("Missing Recaptcha Token");

        dispatch(
          signUpIndividual(
            {
              ...requestModel,
              language: preferencesModel?.lang,
              defaultRegion: preferencesModel?.defaultRegion,
            },
            mobilePhoneModel,
            recaptchaToken,
            preferencesModel?.lang,
            socialToken
          )
        );
      } catch (err) {
        const errMsg =
          typeof err === "string"
            ? err
            : (err as IErrorMessage)?.message || "Unknown error messsage";
        setBrowserError({ message: errMsg });
      }
    }
  };

  const handleSignUp = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    // reset error message to avoid useEffect trigger.
    dispatch(SignUpAction.clearErrorMessage());
    if (currentStep === CurrentSignUpStep.CREDENTIAL) {
      handleAccountInformation();
      return;
    }

    if (currentStep === CurrentSignUpStep.PERSONAL_INFORMATION) {
      handlePersonalInformation();
      return;
    }

    if (currentStep === CurrentSignUpStep.PREFERENCES) {
      handlePreferencesInformation();
      return;
    }
  };

  const handleBack = (stepBack: CurrentSignUpStep) => {
    // reset error message to avoid useEffect trigger.
    dispatch(SignUpAction.clearErrorMessage());
    if (stepBack === CurrentSignUpStep.TYPE) {
      navigate("/SignUp/Select");
      return; // show confirm and back to type.
    }
    setCurrentStep(stepBack);

    if (stepBack === CurrentSignUpStep.PERSONAL_INFORMATION) setStepCount(2);
    else setStepCount(1);
  };

  const handleErrorBackToStep = useCallback(() => {
    const code = errorMessage?.code;
    switch (code) {
      case "8201":
        // Organisation ID is already registered.
        setCurrentStep(CurrentSignUpStep.PERSONAL_INFORMATION);
        setStepCount(2);
        break;
      case "8101":
        // 8101 The email must be unique
        setCurrentStep(CurrentSignUpStep.CREDENTIAL);
        setStepCount(1);
        break;
      default:
        break;
    }
  }, [errorMessage]);

  // @ts-ignore
  const handleMouseDown = (e) => {
    if (e.keyCode === 13) {
      handleSignUp(e); // enter event.
    }
  };

  const handleCallbackFunction = (turnstileToken: string) => {
    switch (tempTurnstileAction.current.action) {
      case "signUpIndividual":
        dispatch(
          signUpIndividual(
            tempTurnstileAction.current.data as IndividualRegistrationRequest,
            tempTurnstileAction.current.mobilePhoneModel,
            turnstileToken,
            tempTurnstileAction.current?.lang,
            tempTurnstileAction.current.socialToken,
            true // for turnstile headers.
          )
        );
        break;
      default:
        break;
    }
    prodModeLog("Finish function " + tempTurnstileAction.current.action);
    // reset after callback called.
    window.turnstile?.reset(turnstileRef.current!); // reset
    tempTurnstileAction.current.action = "";
    tempTurnstileAction.current.data = {};
    tempTurnstileAction.current.mobilePhoneModel = defaultIMobileNumber;
    tempTurnstileAction.current.lang = "";
    tempTurnstileAction.current.socialToken = "";

    setCaptchaLoading(false);
  };

  useEffect(() => {
    executeRecaptchaRef.current = executeRecaptcha;
  }, [executeRecaptcha]);

  useEffect(() => {
    dispatch(getLanguages());
    document.addEventListener("keydown", handleMouseDown);
    return () => {
      document.removeEventListener("keydown", handleMouseDown);
      dispatch(SignUpAction.reset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (errorMessage || currentStep === CurrentSignUpStep.PREFERENCES) {
      // back to current step
      if (errorMessage) handleErrorBackToStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage, currentStep]);

  // handle get params email and token from social login.
  useEffect(() => {
    if (queryParams.get("type") === "google") {
      setIsSocialSignUp(true);
      setEmail(queryParams.get("email") as string);
      setSocialToken(queryParams.get("token") as string);

      setCurrentStep(CurrentSignUpStep.PERSONAL_INFORMATION);
      setStepCount(2);

      window.history.replaceState(null, "", "/Signup/Individual");
    }
  }, [queryParams]);

  // handle get params email and token from social login.
  useEffect(() => {
    if (queryParams.get("type") === "azure") {
      setIsSocialSignUp(true);
      setEmail(queryParams.get("email") as string);
      setSocialToken(queryParams.get("token") as string);

      setCurrentStep(CurrentSignUpStep.PERSONAL_INFORMATION);
      setStepCount(2);

      window.history.replaceState(null, "", "/Signup/Individual");
    }
  }, [queryParams]);

  useEffect(() => {
    const keys = individualResponse ? Object.keys(individualResponse) : [];

    if (isSocialSignUp && keys.indexOf("id") > -1) {
      window.location.href = "/";
      return;
    }

    if (keys.indexOf("id") > -1 && !isSocialSignUp) {
      setShowCompletedRegister(true);
    }
  }, [individualResponse, isSocialSignUp]);

  // recaptcha effect.
  useEffect(() => {
    if (captchaType?.provider === "turnstile") {
      /**
       * The Turnstile token is one-time use — it expires quickly (typically within 2 minutes).
        After sending it to your backend (or after any failed verification), you’ll usually need to refresh / reset the widget to generate a new token.
       */

      const interval = setInterval(() => {
        if (
          typeof window !== "undefined" &&
          window?.turnstile &&
          turnstileRef?.current
        ) {
          // eslint-disable-next-line no-console
          console.log("call interval !!!");
          clearInterval(interval);

          if (turnstileRef.current.hasChildNodes()) {
            // avoid call re-render multiple times
            console.warn(
              "Cloudflare Turnstile has already been rendered. Prevent !!!"
            );
            return;
          }

          window.turnstile?.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            size: "invisible",
            callback: (turnstileToken: string) => {
              if (tempTurnstileAction.current.action !== "") {
                prodModeLog(
                  "Callback token from turnstile, will trigger function " +
                    tempTurnstileAction.current.action
                );
                handleCallbackFunction(turnstileToken);
              } else {
                prodModeLog("No action with callback function !!!");
              }
            },
            "expired-callback": () => {
              setCaptchaLoading(false);
              window.turnstile?.reset(turnstileRef.current!);
            },
            "error-callback": (turnstileErrorCode) => {
              // https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
              setCaptchaLoading(false);
              // eslint-disable-next-line no-console
              console.log({ turnstileErrorCode });
              // window.turnstile?.reset(turnstileRef.current!);

              setTurnstileError(
                `Cloudflare Turnstile get error with code: ${turnstileErrorCode}`
              );
            },
          });
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaType]);

  // @ts-ignore
  return showCompletedRegister ? (
    <SuccessRegister email={model.email} />
  ) : (
    <form>
      <div ref={turnstileRef}></div>
      <BlockUI isBlocked={!sessionHasBeenFetched}>
        <BlockRouter blocked={dirty} />
        <AuthenticationLayout>
          <form>
            {errorMessage &&
              errorMessage?.code !== "8101" &&
              !["RecaptchaRiskException", "RecaptchaInvalidException"].includes(
                errorMessage.type
              ) && <AppAlert.ErrorAlert error={errorMessage} />}
            <SignUpLogo t={t} />
            <SelectDomainType
              isOrg={false}
              onIndividualClick={() => {}}
              onOrgClick={() => {
                navigate("/Signup/Organisation");
              }}
            />
            <StepPath
              wrapperClass="mb-2"
              onBack={handleBack}
              currentStep={currentStep}
              count={stepCount}
            />
            <SignUpCredentialsForm
              errorMessageText={
                errorMessage?.code === "8101" ? errorMessage.message : undefined
              }
              disabled={currentStep !== CurrentSignUpStep.CREDENTIAL}
              wrapperClass={`${
                currentStep !== CurrentSignUpStep.CREDENTIAL
                  ? "visible-hidden"
                  : ""
              }`}
              ref={accountRef}
              email={email}
              isSocialSignUp={isSocialSignUp}
              t={t}
            />
            <PersonalInformationForm
              disabled={currentStep !== CurrentSignUpStep.PERSONAL_INFORMATION}
              wrapperClass={`${
                currentStep !== CurrentSignUpStep.PERSONAL_INFORMATION
                  ? "visible-hidden"
                  : ""
              }`}
              ref={personalInformationRef}
              t={t}
            />
            <PreferencesForm
              disabled={currentStep !== CurrentSignUpStep.PREFERENCES}
              languages={languages}
              wrapperClass={`${
                currentStep !== CurrentSignUpStep.PREFERENCES
                  ? "visible-hidden"
                  : ""
              }`}
              ref={preferencesRef}
              t={t}
            />
            <div
              className={`${
                currentStep !== CurrentSignUpStep.PREFERENCES
                  ? "visible-hidden"
                  : ""
              }`}
            >
              <LaddaButton
                loading={loading || captchaLoading}
                disabled={!eulaSignUpAgreement}
                className="btn btn-primary w-100 mt-2"
                onClickHandler={handleSignUp}
                btnName={t("login:signup")}
              />
              <TermAndServiceWarning t={t} />
            </div>
            {currentStep !== CurrentSignUpStep.PREFERENCES && (
              <button
                type="button"
                onClick={handleSignUp}
                className="btn btn-primary w-100 mt-2"
              >
                {t("next")}
              </button>
            )}
            {currentStep === CurrentSignUpStep.PREFERENCES &&
              errorMessage &&
              ["RecaptchaRiskException", "RecaptchaInvalidException"].includes(
                errorMessage.type
              ) && (
                <>
                  <div className="form-group m-t-xs m-b-none">
                    <AppAlert.ErrorAlert
                      error={errorMessage}
                      className="alert bg-warning text-white alert-styled-left error-container"
                    />
                  </div>
                </>
              )}
            {currentStep === CurrentSignUpStep.PREFERENCES && browserError && (
              <AppAlert.SimpleAlert
                variant="warning"
                className="m-t-xs"
                message={browserError?.message}
              />
            )}

            {currentStep === CurrentSignUpStep.PREFERENCES &&
              turnstileError && (
                <AppAlert.SimpleAlert
                  variant="warning"
                  className="m-t-xs"
                  message={turnstileError}
                />
              )}
            <RecaptchaTerm className="m-t-xs" />
            <TermAndServiceModal
              t={t}
              closeAction={() => {
                dispatch(SignUpAction.showSignUpEulaAgreementInfo(false));
              }}
            />
          </form>
        </AuthenticationLayout>
      </BlockUI>
    </form>
  );
};
